/**
 * dataLayer tracking for The Bradery.
 *
 * Pushes a single technical dataLayer event, `tb_track`, that carries the
 * analytics event name and its flat properties. One GTM trigger and one tag
 * forward it to Heap, so adding an event never requires a GTM change.
 * Spec: docs/specs/tracking-datalayer.md
 *
 * Safety: never calls preventDefault, never mutates the DOM and swallows its
 * own errors, so tracking can never block navigation or the commerce flow.
 */
(function () {
  if (window.tbAnalytics) return;

  var EVENT_NAME_PATTERN = /^[a-z0-9_]+$/;
  var MAX_STRING_LENGTH = 1024;
  var MAX_QUEUE_LENGTH = 50;
  var GTM_WAIT_INTERVAL_MS = 250;
  var GTM_WAIT_ATTEMPTS = 40;

  /**
   * Product click event name for each instrumented surface, kept in one place
   * so a naming change stays a one-line edit. The hero rail is the feature grid
   * rendered in the hero, hence the shared event.
   */
  var PRODUCT_CLICK_EVENTS = {
    home_feature_grid: 'web_feature_grid_product_clicked',
    home_hero: 'web_feature_grid_product_clicked',
    home_selections: 'web_selection_product_clicked'
  };

  /** Quick add event names, grouped for the same reason. */
  var QUICK_ADD_EVENTS = {
    opened: 'web_quick_add_opened',
    completed: 'web_quick_add_completed'
  };

  var SIZE_CLICKED_EVENT = 'web_size_clicked';

  /** Homepage tab bar click, the web counterpart of the app's home_category_pressed. */
  var HOME_TAB_CLICKED_EVENT = 'web_home_category_clicked';

  /** Click on a sale card of the homepage showcase (hero, hero-compact, feature grid, grid). */
  var HOME_SALE_CLICKED_EVENT = 'web_home_sale_clicked';

  /**
   * Card control a quick add started from, sent as `trigger`. The size row
   * shows on hover where the card opts in: a size opens the modal on that size
   * or, for a single-option product, adds it without a modal.
   */
  var QUICK_ADD_TRIGGERS = {
    button: 'button',
    size: 'size',
    sizeOverflow: 'size_overflow',
    sizeDirect: 'size_direct'
  };

  var queue = [];
  var gtmWaitTimer = null;
  var quickAddOrigin = null;

  function isThemeEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function analyticsAllowed() {
    return !!(window.tbConsent && window.tbConsent.analytics());
  }

  function environment() {
    var theme = window.Shopify && window.Shopify.theme;
    return theme && theme.role === 'main' ? 'live' : 'preview';
  }

  /**
   * GTM handles dataLayer messages in order. A push that lands after the
   * `gtm.js` message is handled after the All Pages tags, the Heap loader
   * included, so the Heap tag never runs before Heap exists.
   */
  function gtmStarted() {
    var layer = window.dataLayer;
    if (!Array.isArray(layer)) return false;
    for (var i = 0; i < layer.length; i++) {
      if (layer[i] && layer[i].event === 'gtm.js') return true;
    }
    return false;
  }

  /** Heap accepts flat scalar values only: objects are dropped, strings capped. */
  function sanitize(properties) {
    var clean = {};
    if (!properties || typeof properties !== 'object') return clean;
    Object.keys(properties).forEach(function (key) {
      var value = properties[key];
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'string') {
        clean[key] = value.slice(0, MAX_STRING_LENGTH);
      } else if (typeof value === 'number') {
        if (isFinite(value)) clean[key] = value;
      } else if (typeof value === 'boolean') {
        clean[key] = value;
      }
    });
    return clean;
  }

  /**
   * GTM merges successive pushes into its data model, so the properties of one
   * event would leak into the next. Resetting the key to null first (a push
   * without `event`, which fires nothing) is the documented way to clear it.
   */
  function send(name, properties) {
    var layer = (window.dataLayer = window.dataLayer || []);
    layer.push({ tb_event_properties: null });
    layer.push({ event: 'tb_track', tb_event_name: name, tb_event_properties: properties });
  }

  function flushQueue() {
    while (queue.length) {
      var item = queue.shift();
      send(item.name, item.properties);
    }
  }

  function stopWaitingForGtm() {
    clearInterval(gtmWaitTimer);
    gtmWaitTimer = null;
  }

  function waitForGtm() {
    if (gtmWaitTimer) return;
    var attempts = 0;
    gtmWaitTimer = setInterval(function () {
      attempts += 1;
      if (gtmStarted()) {
        stopWaitingForGtm();
        flushQueue();
      } else if (attempts >= GTM_WAIT_ATTEMPTS) {
        // GTM blocked or absent: drop the queue rather than grow it forever.
        stopWaitingForGtm();
        queue.length = 0;
      }
    }, GTM_WAIT_INTERVAL_MS);
  }

  function track(name, properties) {
    try {
      if (isThemeEditor() || !analyticsAllowed()) return;
      if (typeof name !== 'string' || !EVENT_NAME_PATTERN.test(name)) return;

      var payload = sanitize(properties);
      payload.env = environment();

      if (gtmStarted()) {
        stopWaitingForGtm();
        flushQueue();
        send(name, payload);
        return;
      }
      if (queue.length < MAX_QUEUE_LENGTH) queue.push({ name: name, properties: payload });
      waitForGtm();
    } catch (error) {
      // Tracking must never break the page.
    }
  }

  function toNumber(value) {
    if (value === undefined || value === '') return undefined;
    var number = Number(value);
    return isFinite(number) ? number : undefined;
  }

  /** Reads the attributes rendered by snippets/tb-product-card-tracking.liquid. */
  function productCardProperties(card) {
    var data = card.dataset;
    return {
      product_id: data.tbProductId,
      variant_id: data.tbVariantId,
      product_handle: data.tbProductHandle,
      product_name: data.tbProductName,
      brand_name: data.tbBrandName,
      position: toNumber(data.tbPosition),
      price: toNumber(data.tbPrice),
      currency: data.tbCurrency,
      collection_id: data.tbCollectionId,
      collection_handle: data.tbCollectionHandle,
      source: data.tbSource
    };
  }

  /**
   * Capture phase on purpose: Horizon components may stop propagation on their
   * own click handlers, which would hide the click from a bubble listener.
   */
  function onClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var link = target.closest('.product-card-alt__link');
      if (!link) return;

      var card = link.closest('[data-tb-source]');
      if (!card) return;

      var name = PRODUCT_CLICK_EVENTS[card.dataset.tbSource];
      if (name) track(name, productCardProperties(card));
    } catch (error) {
      // Tracking must never break the click it observes.
    }
  }

  /**
   * Every tab of the homepage sales grid counts, whether it filters in place
   * or navigates. `category_name` is the label as displayed, so it follows the
   * visitor's locale; `tab_type` and `category` are the stable keys.
   */
  function onHomeTabClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var tab = target.closest('.operation-grid__tab');
      if (!tab) return;

      var grid = tab.closest('operation-grid');
      if (!grid || grid.dataset.isHomepage !== 'true') return;

      track(HOME_TAB_CLICKED_EVENT, {
        category_name: tab.textContent.trim(),
        tab_type: tab.dataset.tabType,
        category: tab.dataset.tabCategory
      });
    } catch (error) {
      // Tracking must never break the click it observes.
    }
  }

  /**
   * Click on a sale card of the homepage showcase. The card root carries
   * `data-tb-placement`, `data-tb-position` and `data-tb-operation-id`
   * (snippets/operation-card.liquid); only the card link counts, so a click on
   * a product of the hero rail keeps its own event. `video` is true only when
   * the card's video reached `playing` (`data-playing`, set by
   * assets/operation-card-video.js), false in every other case.
   * `confidence_visitor_id` is the id the Confidence SDK randomises on
   * (`window.tbFlags.visitorId()`), the only key that joins this click to the
   * `home-site-video` assignment in the warehouse: Heap's own identity is not
   * matched to Confidence. Absent when the SDK is not loaded or storage is
   * unavailable, which `sanitize` drops.
   */
  function confidenceVisitorId() {
    try {
      var flags = window.tbFlags;
      return flags && typeof flags.visitorId === 'function' ? flags.visitorId() : undefined;
    } catch (error) {
      return undefined;
    }
  }

  function onHomeSaleClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var link = target.closest('.operation-card__link, .operation-card__cta');
      if (!link) return;

      var card = link.closest('.operation-card[data-tb-placement]');
      if (!card) return;

      track(HOME_SALE_CLICKED_EVENT, {
        confidence_visitor_id: confidenceVisitorId(),
        operation_id: card.dataset.tbOperationId,
        placement: card.dataset.tbPlacement,
        position: toNumber(card.dataset.tbPosition),
        video: !!card.querySelector('operation-card-video[data-playing]')
      });
    } catch (error) {
      // Tracking must never break the click it observes.
    }
  }

  /**
   * Collection Voyage clicks. On the Voyage home a click on a collection card
   * counts as `travel_collection_card_clicked`; on the collection page a click on
   * a voyage card counts as `travel_collection_voyage_clicked`. Both cards carry
   * the collection as data attributes (snippets/voyage-card.liquid). The voyage id
   * is the travel handle `voyage-<operationId>` without its prefix.
   */
  function onTravelCollectionClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var card = target.closest('.voyage-overview__card-item[data-collection-id]');
      if (!card || !card.querySelector('a.voyage-overview__card').contains(target)) return;

      var collectionId = card.dataset.collectionId;
      var collectionName = card.dataset.collectionName;

      if (card.dataset.voyageType === 'collection') {
        track('travel_collection_card_clicked', { collection_id: collectionId, collection_name: collectionName });
        return;
      }

      var params = new URLSearchParams(window.location.search);
      track('travel_collection_voyage_clicked', {
        collection_id: collectionId,
        collection_name: collectionName,
        voyage_id: (card.dataset.voyageId || '').replace(/^voyage-/, ''),
        channel: params.get('source') === 'app' ? 'app' : 'web'
      });
    } catch (error) {
      // Tracking must never break the click it observes.
    }
  }

  /**
   * Trigger of a click on a card control, or null when the click starts
   * nothing. Mirrors the size click handler of quick-add-alternate.js: a
   * disabled or loading size does nothing, the "…" and a multi-option size open
   * the modal, a single-option size adds straight to the cart.
   */
  function quickAddTrigger(control) {
    if (control.classList.contains('product-card-alt__quick-add')) return QUICK_ADD_TRIGGERS.button;
    if (control.disabled || control.classList.contains('is-loading')) return null;
    if (control.classList.contains('product-card-alt__size--more')) return QUICK_ADD_TRIGGERS.sizeOverflow;

    var row = control.closest('.product-card-alt__sizes');
    return row && row.dataset.sizeMode === 'modal' ? QUICK_ADD_TRIGGERS.size : QUICK_ADD_TRIGGERS.sizeDirect;
  }

  /**
   * The quick add modal is appended to the body, outside the card, so the card
   * an add came from is the one whose control opened that modal. Every click
   * that opens it overwrites the origin, an untracked card clears it. A direct
   * size add opens no modal and leaves the origin alone.
   */
  function onQuickAddClick(event) {
    try {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var control = target.closest('.product-card-alt__quick-add, .product-card-alt__size');
      if (!control) return;

      var trigger = quickAddTrigger(control);
      if (!trigger) return;

      var card = control.closest('[data-tb-source]');
      var properties = card ? productCardProperties(card) : null;

      if (properties && (trigger === QUICK_ADD_TRIGGERS.size || trigger === QUICK_ADD_TRIGGERS.sizeDirect)) {
        track(
          SIZE_CLICKED_EVENT,
          Object.assign({}, properties, {
            variant_id: 'gid://shopify/ProductVariant/' + control.dataset.variantId,
            size: control.textContent.trim(),
            trigger: trigger
          })
        );
      }
      if (trigger === QUICK_ADD_TRIGGERS.sizeDirect) return;

      quickAddOrigin = properties ? Object.assign(properties, { trigger: trigger }) : null;
      if (quickAddOrigin) track(QUICK_ADD_EVENTS.opened, quickAddOrigin);
    } catch (error) {
      // Tracking must never break the click it observes.
    }
  }

  /**
   * Adds the variant a cart add posted and the quantity to its origin. The
   * add reports that variant as sourceId. A form input can lag behind it when
   * the add was queued during a variant change, so it is only a fallback.
   */
  function withAddedItem(origin, detail, input) {
    var sourceId = String(detail.sourceId || '');
    var variantId = /^\d+$/.test(sourceId) ? sourceId : input && input.value;

    origin.quantity = toNumber(detail.data.itemCount);
    if (variantId) origin.variant_id = 'gid://shopify/ProductVariant/' + variantId;
    return origin;
  }

  /**
   * Properties of the tracked card a successful cart add came from, or null.
   * Two adds qualify: one dispatched on a size of a tracked card (direct add),
   * and one posted by the quick add modal for the product of the card that
   * opened it. Read-only, so every cart:update listener gets the same answer
   * whatever the listener order.
   */
  function cartAddOrigin(event) {
    try {
      var detail = event && event.detail;
      var data = detail && detail.data;
      var element = event && event.target;
      if (!data || data.didError) return null;
      if (!element || typeof element.closest !== 'function') return null;

      if (element.closest('.product-card-alt__size')) {
        var card = element.closest('[data-tb-source]');
        if (!card) return null;
        var direct = Object.assign(productCardProperties(card), { trigger: QUICK_ADD_TRIGGERS.sizeDirect });
        return withAddedItem(direct, detail, null);
      }

      if (!quickAddOrigin || !element.closest('quick-add-alt-dialog')) return null;
      if (quickAddOrigin.product_id !== 'gid://shopify/Product/' + data.productId) return null;
      return withAddedItem(Object.assign({}, quickAddOrigin), detail, element.querySelector('input[name="id"]'));
    } catch (error) {
      return null;
    }
  }

  function onCartUpdate(event) {
    var origin = cartAddOrigin(event);
    if (origin) track(QUICK_ADD_EVENTS.completed, origin);
  }

  window.tbAnalytics = { track: track, cartAddOrigin: cartAddOrigin };
  document.addEventListener('click', onClick, { capture: true, passive: true });
  document.addEventListener('click', onQuickAddClick, { capture: true, passive: true });
  document.addEventListener('click', onHomeTabClick, { capture: true, passive: true });
  document.addEventListener('click', onTravelCollectionClick, { capture: true, passive: true });
  document.addEventListener('click', onHomeSaleClick, { capture: true, passive: true });
  document.addEventListener('cart:update', onCartUpdate);
  // A middle click opens the product or the tab's link in a new tab without firing `click`.
  document.addEventListener(
    'auxclick',
    function (event) {
      if (event.button !== 1) return;
      onClick(event);
      onHomeTabClick(event);
      onTravelCollectionClick(event);
      onHomeSaleClick(event);
    },
    { capture: true, passive: true }
  );
})();
